// The shared TERRA heightmap texture — the keystone that makes both the water shader and (later) the 3D
// grass one-pass. We already have the field's node heights on the CPU (f.heights); baking them into one
// tiny GPU texture lets the WATER fragment read floor height for depth-tint + shoreline with NO depth
// render target, and lets GRASS conform to the surface in the vertex shader with NO cpu raycasts.
//
// Encoding (RGBA8, LinearFilter):
//   R = ground height remapped to [hMin,hMax] in a SINGLE 8-bit channel. NOT packed across RGB — a 24-bit
//       split breaks under linear filtering (independent per-channel lerp spikes at every byte boundary).
//       One channel over the ~120–430-stud garden span is ~1.2-stud quantization, bilinear-smoothed to
//       <0.6 stud — ample for a smooth water-depth gradient and grass placement.
//   A = garden grass-band mask (1 exactly where vertColor paints grass), for the grass placement gate.

import * as THREE from "three";
import type { TerraField } from "./field";

export interface HeightTex {
  tex: THREE.DataTexture;
  hMin: number;
  hMax: number;
}

export function buildHeightTex(f: TerraField): HeightTex {
  const N = f.n + 1; // heights is (n+1)², row-major i = gz*N + gx (matches the arena vertex order)
  const data = new Uint8Array(N * N * 4);
  let hMin = Infinity, hMax = -Infinity;
  for (let i = 0; i < f.heights.length; i++) {
    const h = f.heights[i];
    if (h < hMin) hMin = h;
    if (h > hMax) hMax = h;
  }
  const span = Math.max(hMax - hMin, 1e-3);
  for (let i = 0; i < N * N; i++) {
    const h = f.heights[i];
    data[i * 4] = Math.max(0, Math.min(255, Math.round(((h - hMin) / span) * 255)));
    // grass mask: match vertColor's garden grass band EXACTLY (y=h-200 in [-14,90) → h in [186,290)),
    // above the waterline, and never on built pads. (seaY=174, so 186 also clears the beach-sand band.)
    const grass = f.style === 0 && h >= 186 && h < 290 && f.pad[i] < 0.05 ? 255 : 0;
    data[i * 4 + 3] = grass;
  }
  const tex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return { tex, hMin, hMax };
}
